/**
 * Detection utilities for YOLO-based detection (pickleball, soccer)
 *
 * IMPORTANT: This module contains the normalization layer that ensures
 * live counting and replay use IDENTICAL data formats.
 *
 * Data flow:
 * - Live:   YOLO → buildFrameData() → normalizeFrameForCounter() → counter.update()
 * - Replay: Recording frame       → normalizeFrameForCounter() → counter.update()
 *
 * The normalization layer is the SINGLE POINT where format conversion happens.
 *
 * CANONICAL FORMAT (both recordings and counters use this):
 * { ball: {x, y, width, height, confidence}, paddle: {...}, keypoints: [...] }
 */

const CONFIDENCE_THRESHOLD = 0.3;

/**
 * Unwrap a detection from bbox format to flat format (if needed)
 *
 * bbox format:  { bbox: { x, y, width, height }, confidence }
 * flat format:  { x, y, width, height, confidence }
 *
 * @param {Object|null} detection - Detection in either format
 * @returns {Object|null} Detection in flat format
 */
export function unwrapDetectionBbox(detection) {
  if (!detection) return null;
  if (!detection.bbox) return detection; // Already flat

  const { bbox, confidence, ...rest } = detection;
  return {
    x: bbox.x,
    y: bbox.y,
    width: bbox.width,
    height: bbox.height,
    confidence,
    ...rest
  };
}

/**
 * Normalize a frame to counter-expected format - THIN LAYER
 *
 * This is the SINGLE function that converts any frame format to what counters expect.
 * Both live counting and replay MUST pass through this function.
 *
 * Input formats accepted:
 * - Recording: { ball: {x, y, width, height, confidence}, paddle: {...}, keypoints: [...] }
 * - Live (bbox): { ball: {bbox: {...}, confidence}, paddle: {...} }
 *
 * Output format (canonical flat format used by counters):
 * - { ball: {x, y, width, height, confidence}, paddle: {...}, keypoints: [...] }
 *
 * @param {Object} frameData - Frame data from recording or buildFrameData()
 * @returns {Object} Normalized frame data for counter
 */
export function normalizeFrameForCounter(frameData) {
  if (!frameData) return null;

  return {
    ...frameData,
    ball: unwrapDetectionBbox(frameData.ball),
    paddle: unwrapDetectionBbox(frameData.paddle)
    // keypoints passed through unchanged (used by soccer/skipping)
  };
}

/**
 * Get a detection by class name with minimum confidence
 *
 * IMPORTANT: This function expects NORMALIZED frame data (from normalizeFrameForCounter).
 * The frame data should be in flat format: { ball: {x, y, width, height, confidence} }
 *
 * @param {Object} normalizedFrame - Normalized frame data in flat format
 * @param {string} className - Class name to find ('ball', 'paddle', etc.)
 * @param {number} minConfidence - Minimum confidence threshold
 * @returns {Object|null} Detection object { x, y, width, height, confidence } or null
 */
export function getDetection(normalizedFrame, className, minConfidence = CONFIDENCE_THRESHOLD) {
  if (!normalizedFrame) return null;

  const detection = normalizedFrame[className.toLowerCase()];
  if (!detection) return null;

  // Check confidence threshold (undefined confidence passes - for backwards compatibility)
  if (detection.confidence !== undefined && detection.confidence < minConfidence) {
    return null;
  }

  return detection;
}

/**
 * Calculate distance between two points
 * @param {Object} pointA - {x, y}
 * @param {Object} pointB - {x, y}
 * @returns {number} Distance in pixels
 */
export function calculateDistance(pointA, pointB) {
  if (!pointA || !pointB) return Infinity;
  return Math.sqrt(
    Math.pow(pointB.x - pointA.x, 2) +
    Math.pow(pointB.y - pointA.y, 2)
  );
}

/**
 * Calculate Intersection over Union (IoU) of two bounding boxes
 * @param {Object} boxA - {x, y, width, height}
 * @param {Object} boxB - {x, y, width, height}
 * @returns {number} IoU value (0-1)
 */
export function calculateIoU(boxA, boxB) {
  if (!boxA || !boxB) return 0;

  // Calculate intersection
  const xA = Math.max(boxA.x, boxB.x);
  const yA = Math.max(boxA.y, boxB.y);
  const xB = Math.min(boxA.x + boxA.width, boxB.x + boxB.width);
  const yB = Math.min(boxA.y + boxA.height, boxB.y + boxB.height);

  const intersectionWidth = Math.max(0, xB - xA);
  const intersectionHeight = Math.max(0, yB - yA);
  const intersectionArea = intersectionWidth * intersectionHeight;

  if (intersectionArea === 0) return 0;

  // Calculate union
  const areaA = boxA.width * boxA.height;
  const areaB = boxB.width * boxB.height;
  const unionArea = areaA + areaB - intersectionArea;

  return intersectionArea / unionArea;
}
